import { APP_PATHS } from '@norum/contracts';
import { ApiError, failureFrom } from './errors';

/**
 * Every call the interface makes (docs/reference/frontend-contract.md).
 *
 * One function. `POST /v1/app` plus the work-api path, a JSON body, and the
 * session cookie the browser already holds. **Nothing here sends `humanId`,
 * `actor` or `department`** — the gateway writes them from the session, and
 * a body that carried them would be ignored rather than believed. The
 * property the whole boundary rests on is that this file has no way to say
 * who is asking.
 *
 * The path is checked against the platform's own allow-list when the module
 * that names it loads (`appPath` throws), so a screen asking for a route
 * that does not exist fails the first test that imports it rather than
 * 404ing at the edge. It is a runtime check, not a compile-time one: the
 * list is a `Set`, and a type cannot be read off it.
 */

/** A path a person may reach. The set is the platform's; this is its type. */
export type AppPath = string & { readonly __appPath: unique symbol };

export function appPath(path: string): AppPath {
  if (!APP_PATHS.has(path)) {
    throw new Error(
      `${path} is not on the list of paths a person may reach (packages/contracts/src/app-paths.ts).`,
    );
  }
  return path as AppPath;
}

const FORBIDDEN_IN_BODY = ['humanId', 'actor', 'department', 'decidedByHuman'] as const;

export async function post<T>(path: AppPath, body: Record<string, unknown> = {}): Promise<T> {
  for (const key of FORBIDDEN_IN_BODY) {
    if (key in body) {
      // Not a security control — the gateway overwrites these — but a
      // developer control: a screen that sends one is writing code that
      // does nothing and looks like it does something.
      throw new Error(`a call never names "${key}"; the gateway stamps it from the session`);
    }
  }
  let response: Response;
  try {
    response = await fetch(`/v1/app${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError({ kind: 'offline' });
  }
  if (!response.ok) throw new ApiError(await failureFrom(response, path));
  return (await response.json()) as T;
}

/**
 * The two things the interface reads from the MCP door (ADR-0089): which
 * tools a client gets, and which paths are held back and why. With the
 * browser's own cookie, which the gateway accepts there as it does a token.
 *
 * Read methods only, and by type: the interface never calls a tool through
 * this door. A person already reaches every one of them through `post`, and
 * a second way to make the same call would be a second thing to keep right.
 */
export type McpReadMethod = 'tools/list' | 'norum/withheld';

export async function mcpRead<T>(method: McpReadMethod): Promise<T> {
  let response: Response;
  try {
    response = await fetch('/v1/mcp', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method }),
    });
  } catch {
    throw new ApiError({ kind: 'offline' });
  }
  if (!response.ok) throw new ApiError(await failureFrom(response, '/v1/mcp'));
  const envelope = (await response.json()) as { result?: T; error?: { code: number; message: string } };
  if (!envelope.result) {
    // A JSON-RPC error is a 200 by design; here it can only mean a gateway
    // older than this screen, which is a deployment gap and said as one.
    throw new ApiError({
      kind: 'misconfigured',
      detail: envelope.error?.message ?? 'The MCP door answered without a result.',
    });
  }
  return envelope.result;
}
